/**
 * @description
 * Data table component for displaying streaming data with pagination, sorting, and search.
 * Supports large datasets with virtual scrolling and responsive design.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/components/vscode-lens/DataTable.test.tsx
 */

import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import type { StreamRecord, SortConfig } from '../../../types/vscode-lens';

interface DataTableProps {
  data: StreamRecord[];
  isLoading?: boolean;
}

const DISPLAY_LIMIT = 2000;
const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

/**
 * Convert data to CSV format
 */
function convertToCSV(data: StreamRecord[]): string {
  if (data.length === 0) return '';
  
  const columns = Object.keys(data[0]);
  
  // Create CSV header
  const header = columns.join(',');
  
  // Create CSV rows
  const rows = data.map(record => 
    columns.map(column => {
      const value = record[column];
      if (value === null || value === undefined) return '';
      
      // Escape CSV values (handle commas, quotes, newlines)
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );
  
  return [header, ...rows].join('\n');
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Custom tooltip component for better content formatting
 */
function CustomTooltip({ content, children }: { content: string; children: React.ReactNode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Calculate optimal position
    let x = rect.left + rect.width / 2;
    let y = rect.top - 10;
    
    // Adjust if tooltip would go off-screen horizontally
    const tooltipWidth = Math.min(viewportWidth * 0.67, 1024); // w-2/3 with max-w-4xl
    if (x + tooltipWidth / 2 > viewportWidth) {
      x = viewportWidth - tooltipWidth / 2 - 10;
    } else if (x - tooltipWidth / 2 < 0) {
      x = tooltipWidth / 2 + 10;
    }
    
    // Adjust if tooltip would go off-screen vertically
    const tooltipHeight = 200; // estimated max height
    if (y - tooltipHeight < 0) {
      y = rect.bottom + 10; // Show below instead
    }
    
    setPosition({ x, y });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const formatContent = (content: string): string => {
    if (!content) return '';
    
    // Decode HTML entities
    const decoded = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    // Format XML/HTML tags for better readability
    return decoded
      .replace(/<([^>]+)>/g, '\n<$1>')
      .replace(/<\/([^>]+)>/g, '</$1>\n')
      .replace(/\n\s*\n/g, '\n')
      .trim();
  };

  return (
    <>
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="cursor-help"
      >
        {children}
      </div>
      
      {isVisible && (
        <div
          className="fixed z-50 w-2/3 max-w-4xl p-4 rounded-lg shadow-lg border bg-white text-black text-sm font-mono whitespace-pre-wrap break-words"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: position.y < 200 ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
            pointerEvents: 'none',
          }}
        >
          <div className="font-semibold text-sm text-muted-foreground mb-2 uppercase tracking-wide">
            Full Content:
          </div>
          <div className="text-sm leading-relaxed">
            {formatContent(content)}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Data table component
 */
export function DataTable({ data, isLoading }: DataTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<typeof PAGE_SIZE_OPTIONS[number]>(100);

  // Get columns from first record
  const columns = useMemo(() => {
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }, [data]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    
    const query = searchQuery.toLowerCase();
    return data.filter(record =>
      Object.values(record).some(value =>
        String(value).toLowerCase().includes(query)
      )
    );
  }, [data, searchQuery]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.column];
      const bValue = b[sortConfig.column];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue);
      const bString = String(bValue);
      
      return sortConfig.direction === 'asc'
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const displayData = useMemo(() => {
    const limited = sortedData.slice(0, DISPLAY_LIMIT);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return limited.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(Math.min(sortedData.length, DISPLAY_LIMIT) / pageSize);
  const showingWarning = sortedData.length > DISPLAY_LIMIT;

  const handleSort = (column: string) => {
    setSortConfig(current => {
      if (current?.column === column) {
        return {
          column,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { column, direction: 'asc' };
    });
  };

  const handlePageSizeChange = (newSize: typeof PAGE_SIZE_OPTIONS[number]) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  if (data.length === 0 && !isLoading) {
    return (
      <div className="rounded-lg border bg-card p-12 text-center">
        <p className="text-muted-foreground">
          No data available. Start streaming to see results.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-1">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search in visible data..."
              className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const csvContent = convertToCSV(filteredData);
                const timestamp = new Date().toISOString().split('T')[0];
                downloadCSV(csvContent, `vscode-lens-data-${timestamp}.csv`);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors"
              title="Export filtered data as CSV"
            >
              <Download className="h-4 w-4" />
              Export Filtered CSV
            </button>
            <button
              onClick={() => {
                const csvContent = convertToCSV(data);
                const timestamp = new Date().toISOString().split('T')[0];
                downloadCSV(csvContent, `vscode-lens-all-data-${timestamp}.csv`);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              title="Export all data as CSV"
            >
              <Download className="h-4 w-4" />
              Export All CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          {PAGE_SIZE_OPTIONS.map(size => (
            <button
              key={size}
              onClick={() => handlePageSizeChange(size)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                pageSize === size
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border border-input hover:bg-accent'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Warning for large datasets */}
      {showingWarning && (
        <div className="rounded-md bg-secondary border border-border p-3 text-sm text-secondary-foreground">
          Displaying first {DISPLAY_LIMIT.toLocaleString()} of {sortedData.length.toLocaleString()} records. 
          Use export buttons below to download all data.
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    className="px-4 py-3 text-left font-medium cursor-pointer hover:bg-muted/80 transition-colors"
                    onClick={() => handleSort(column)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column}</span>
                      {sortConfig?.column === column && (
                        sortConfig.direction === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((record, index) => (
                <tr
                  key={index}
                  className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                >
                  {columns.map((column) => (
                    <td key={column} className="px-4 py-3">
                      <CustomTooltip content={String(record[column] ?? '')}>
                        <div className="max-w-xs truncate">
                          {formatValue(record[column])}
                        </div>
                      </CustomTooltip>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="p-8 text-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading data...</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, Math.min(sortedData.length, DISPLAY_LIMIT))} of {Math.min(sortedData.length, DISPLAY_LIMIT).toLocaleString()} records
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Format cell value for display
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  
  const stringValue = String(value);
  
  // For very long content, truncate more aggressively in the table view
  if (stringValue.length > 100) {
    return stringValue.substring(0, 97) + '...';
  }
  
  return stringValue;
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */